import { Lock, Plus, ShieldCheck, Unlock } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Explainer } from "../components/Explainer";
import { ApprovedListNote, MarketResults, type ApprovedListStatus } from "../components/MarketResults";
import { Pill } from "../components/Pill";
import { RoleButton, RoleGroup } from "../components/RequireRole";
import { TableScroll } from "../components/TableScroll";
import { fieldLabel, lifecycleLabel, words } from "../lib/words";
import { Empty, ErrorState, Loading } from "../components/States";
import { LastTimeBuy } from "../components/uc5/LastTimeBuy";
import { ManualEntry } from "../components/uc5/ManualEntry";
import { OpenCase } from "../components/uc5/OpenCase";
import { Outreach } from "../components/uc5/Outreach";
import { AssetLink, sourceAsset } from "../components/register/AssetLink";
import { SourceLink } from "../components/SourceLink";
import { useMe, type Me } from "../lib/me";
import { uc5, type Job, type ObsolescenceFinding, type UC5Job } from "../lib/api";
import {
  approveAndOpenStage2, checkObsolescence, checkOnOpen, fetchApprovedListStatus, marketSearched, startManual,
  type ManualForm,
} from "./vendorFlow";

const STAGES = [
  { key: "spec_sourcing", n: "Stage 1", t: "Specification Sourcing" },
  { key: "obsolescence", n: "Stage 2", t: "Obsolescence Check" },
  { key: "market", n: "Stage 3", t: "Market Search Results" },
];

export function Vendor({ uc1Jobs, jobId, onSelect }: {
  uc1Jobs: Job[]; refresh: () => void;
  /** The job in the URL, `/vendor/:jobId`. */
  jobId?: string;
  /** Called when the analyst opens another job, so the URL follows. */
  onSelect?: (id: string) => void;
}) {
  const [jobs, setJobs] = useState<UC5Job[]>([]);
  const [job, setJob] = useState<UC5Job | null>(null);
  const [openNew, setOpenNew] = useState(false);
  const [manual, setManual] = useState<ManualForm>({
    manufacturer: "", model_number: "", part_number: "", description: "",
  });
  const [listState, setListState] = useState<"loading" | "ready" | Error>("loading");
  const [entryMode, setEntryMode] = useState<"uc1" | "manual">("uc1");
  const [fromJob, setFromJob] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [stage2Error, setStage2Error] = useState<string | null>(null);
  const selected = useRef<string | null>(null);
  const { me } = useMe();
  const listStatus = useQuery({ queryKey: ["approved-vendor-list"], queryFn: fetchApprovedListStatus, staleTime: 60_000 });
  const source = job ? sourceAsset(job, uc1Jobs) : null;
  useEffect(() => { selected.current = job?.id ?? null; }, [job]);

  const refresh = () => uc5.list()
    .then((js) => { setJobs(js); setListState("ready"); })
    .catch((e: Error) => setListState((s) => (s === "ready" ? s : e)));
  useEffect(() => { refresh(); }, []);

  async function create() {
    setBusy(true); setError(null);
    try {
      const j = entryMode === "uc1" && fromJob
        ? await uc5.fromJob(fromJob)
        : await startManual(manual);
      setJob(j); setStage2Error(null); setOpenNew(false); refresh();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  async function approve() {
    if (!job) return;
    setBusy(true); setError(null); setStage2Error(null);
    const o = Object.entries(overrides).map(([name, value]) => ({ name, value }));
    const out = await approveAndOpenStage2(job, o);
    setJob(out.job); setError(out.error); setStage2Error(out.stage2Error); setBusy(false);
    if (!out.error) { setOverrides({}); refresh(); }
  }

  async function runCheck(target: UC5Job | null = job) {
    if (!target) return;
    setBusy(true); setStage2Error(null);
    const out = await checkObsolescence(target);
    setBusy(false);
    if (!out) return;
    // The analyst may have opened another job while this one was being checked.
    if (selected.current === out.job.id) { setJob(out.job); setStage2Error(out.error); }
    if (!out.error) refresh();
  }

  function open(j: UC5Job) {
    selected.current = j.id; setJob(j); setStage2Error(null);
    if (checkOnOpen(j)) void runCheck(j);
  }

  // The URL follows the open job, and an address naming a job opens it. Each side acts only on
  // its own change, so the back button is not undone by the job still on screen.
  const lastSelected = useRef<string | null>(null);
  useEffect(() => {
    const id = job?.id ?? null;
    if (id === lastSelected.current) return;
    lastSelected.current = id;
    if (id && id !== jobId) onSelect?.(id);
  }, [job, jobId, onSelect]);
  const openRef = useRef(open);
  openRef.current = open;
  useEffect(() => {
    if (!jobId || selected.current === jobId) return;
    let live = true;
    uc5.get(jobId)
      .then((j) => { if (live) { lastSelected.current = j.id; openRef.current(j); } })
      .catch((e: Error) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [jobId]);

  async function search() {
    if (!job) return;
    setBusy(true); setError(null);
    try { setJob(await uc5.marketSearch(job.id)); refresh(); }
    catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  // A last-time-buy decision is taken on the market results, so it reads as the last stage.
  const idx = Math.max(0, STAGES.findIndex((s) => s.key === (job?.stage === "ltb" ? "market" : job?.stage)));
  const approved = Boolean(job?.spec_approved_by);

  return (
    <div className="workspace">
      <section className="pane">
        <div className="joblog-head">
          <strong style={{ fontSize: 17 }}>Resolutions</strong><span className="chip">UC5</span>
        </div>
        <div style={{ marginBottom: 14 }}>
          <RoleButton role="engineer" me={me} style={{ width: "100%" }} onClick={() => setOpenNew(!openNew)}>
            <span className="row" style={{ justifyContent: "center" }}><Plus size={16} /> New Check</span>
          </RoleButton>
        </div>

        {openNew && (
          <div className="card" style={{ marginBottom: 14, padding: 16 }}>
            <div className="stack" style={{ gap: 10 }}>
              <div className="filters" style={{ marginBottom: 0 }}>
                <button type="button" className={`toggle ghost ${entryMode === "uc1" ? "on" : ""}`}
                        aria-pressed={entryMode === "uc1"} onClick={() => setEntryMode("uc1")}>From an extraction</button>
                <button type="button" className={`toggle ghost ${entryMode === "manual" ? "on" : ""}`}
                        aria-pressed={entryMode === "manual"} onClick={() => setEntryMode("manual")}>Enter by hand</button>
              </div>
              {entryMode === "uc1" ? (
                <>
                  <select value={fromJob} onChange={(e) => setFromJob(e.target.value)} aria-label="Extraction to check">
                    <option value="">Select a completed extraction...</option>
                    {uc1Jobs.filter((j) => ["completed", "awaiting_review"].includes(j.status)).map((j) => (
                      <option key={j.id} value={j.id}>
                        J-{j.id.slice(0, 4).toUpperCase()} · {j.tag} · {j.package}
                      </option>
                    ))}
                  </select>
                  <p className="muted" style={{ fontSize: 12 }}>
                    The identity fields are carried across, so the check runs on what was actually
                    read from the document rather than on retyped values.
                  </p>
                  <RoleButton role="engineer" me={me} onClick={create} disabled={busy || !fromJob}>
                    {busy ? "Starting..." : "Start check"}
                  </RoleButton>
                </>
              ) : (
                <ManualEntry form={manual} onChange={setManual} onStart={create} busy={busy} me={me} />
              )}
            </div>
          </div>
        )}

        {listState === "loading" && <Loading what="Loading checks" />}
        {listState instanceof Error && <ErrorState error={listState} onRetry={refresh} />}
        {listState === "ready" && jobs.length === 0 && <Empty title="No checks yet">Start one from an extraction or by hand.</Empty>}
        {jobs.map((j) => (
          <Uc5Card key={j.id} job={j} tag={sourceAsset(j, uc1Jobs)?.tag ?? null} selected={job?.id === j.id} />
        ))}
      </section>

      <section className="detail">
        {error && <div className="card" style={{ marginBottom: 16 }}><span className="pill fail">error</span> {error}</div>}
        {!job ? (
          <div className="empty"><div><ShieldCheck size={40} style={{ opacity: .3 }} />
            <p style={{ marginTop: 12 }}>Select a check, or start a new one.</p></div></div>
        ) : (
          <div className="stack">
            <div className="detail-head">
              <h2>U-{job.id.slice(0, 4).toUpperCase()}</h2>
              <span className="tagchip">{job.identity.model_number}</span>
              <Pill value={job.status} />
              {source && <span style={{ marginLeft: "auto" }}><AssetLink pkg={source.package} tag={source.tag} /></span>}
            </div>

            <div className="card">
              <div className="stages">
                {STAGES.map((s, i) => (
                  <div className={`stage ${i < idx ? "done" : i === idx ? "current" : ""}`} key={s.key}>
                    {i > 0 && <div className="connector" />}
                    <div className="node">{i + 1}</div>
                    <div className="n">{s.n}</div><div className="t">{s.t}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <div className="section-title">
                <span className="stage-chip">Stage 1</span><h3>Specification Sourcing</h3>
              </div>
              <Explainer>
                Sourced the search-critical values for this component from the specification
                region. Nothing here is inferred from the part number. You can correct a value
                before approving: the correction applies to this check only and the original
                extraction record is left untouched.
              </Explainer>
              <SpecTable spec={job.spec ?? []} approved={approved} me={me} overrides={overrides}
                         onOverride={(name, value) => setOverrides({ ...overrides, [name]: value })} />
            </div>

            <div className={approved ? "card" : "mismatch"}>
              {!approved && (
                <div className="bar"><Lock size={16} /> The market search is locked until the specification is approved.</div>
              )}
              <div style={{ padding: approved ? 0 : 16 }}>
                {approved ? (
                  <>
                    <div className="section-title">
                      <span className="stage-chip">Gate</span>
                      <h3 className="row"><Unlock size={18} /> Specification approved</h3>
                    </div>
                    <p className="muted" style={{ fontSize: 14 }}>
                      Approved by {job.spec_approved_by}. The market search may now run.
                    </p>
                  </>
                ) : (
                  <>
                    <p style={{ fontSize: 14, marginBottom: 12 }}>
                      Approving records your name against the specification used for the search
                      and starts the obsolescence check. Nothing external runs before this.
                    </p>
                    <RoleButton role="engineer" me={me} onClick={approve} disabled={busy}>Approve specification</RoleButton>
                  </>
                )}
              </div>
            </div>

            {approved && (
              <ObsolescenceStage job={job} me={me} busy={busy} error={stage2Error} onRun={() => runCheck()} onSearch={search}>
                <OpenCase key={`case-${job.id}`} job={job} me={me} />
              </ObsolescenceStage>
            )}
            {marketSearched(job) && <MarketStage job={job} listStatus={listStatus.data} />}
            {marketSearched(job) && (
              <LastTimeBuy key={`ltb-${job.id}`} job={job} me={me} onJob={(j) => { setJob(j); refresh(); }} />
            )}
            {approved && (
              <Outreach key={`outreach-${job.id}`} job={job} me={me} onJob={(j) => { setJob(j); refresh(); }} />
            )}
          </div>
        )}
      </section>
    </div>
  );
}

/** One check in the list: a link to its URL, naming the tag it was started for. */
export function Uc5Card({ job, tag, selected }: { job: UC5Job; tag: string | null; selected: boolean }) {
  return (
    <Link to={`/vendor/${job.id}`} className={`jobcard ${selected ? "selected" : ""}`} aria-current={selected ? "page" : undefined}>
      <span className="top">
        <span className="id">U-{job.id.slice(0, 4).toUpperCase()}{tag ? ` · ${tag}` : ""}</span><Pill value={job.status} />
      </span>
      <span className="tag">{job.identity.model_number ?? "unknown model"}</span>
      <span className="sub">{job.identity.make_manufacturer ?? job.identity.description ?? ""}</span>
    </Link>
  );
}

type SpecRow = NonNullable<UC5Job["spec"]>[number];

/** The search-critical values, each correctable by an engineer before approval. */
export function SpecTable({ spec, approved, me, overrides, onOverride }: {
  spec: SpecRow[]; approved: boolean; me: Me | null | undefined;
  overrides: Record<string, string>; onOverride: (name: string, value: string) => void;
}) {
  const table = (
      <TableScroll label="Specification" style={{ marginTop: 16 }}>
        <table>
          <thead><tr><th>Parameter</th><th>Value</th><th>Source</th><th>Override</th></tr></thead>
          <tbody>
            {spec.map((s) => (
              <tr key={s.name}>
                <td>{fieldLabel(s.name)}</td>
                <td className="mono">{s.override?.value ?? s.value}</td>
                <td className="muted" style={{ fontSize: 12 }}>
                  {s.override ? `was ${s.override.original} · ${s.override.by}` : s.source}
                </td>
                <td>
                  {approved ? <span className="muted" style={{ fontSize: 12 }}>locked</span> : (
                    <input style={{ width: 150, fontSize: 13 }} placeholder="correct..."
                           aria-label={`Correct ${fieldLabel(s.name)}`}
                           value={overrides[s.name] ?? ""}
                           onChange={(e) => onOverride(s.name, e.target.value)} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
  );
  // Once approved the values are locked for everyone, so there is nothing to gate.
  return approved ? table : <RoleGroup role="engineer" me={me} label="Specification corrections">{table}</RoleGroup>;
}

const STATUS_WORDS: Record<string, { label: string; tone: string }> = {
  obsolete: { label: "obsolete", tone: "fail" },
  announced: { label: "end of life announced", tone: "review" },
  active: { label: "active", tone: "ok" },
  no_notice_found: { label: "no published notice found", tone: "info" },
};

function Finding({ f, lead }: { f: ObsolescenceFinding; lead?: string }) {
  return (
    <li style={{ marginTop: 12 }}>
      {lead && <span className="pill info" style={{ marginRight: 8 }}>{lead}</span>}
      <span className="muted" style={{ fontSize: 12 }}>
        {words(f.authority)}{f.lifecycle ? ` · ${lifecycleLabel(f.lifecycle)}` : ""}
      </span>
      <blockquote style={{ margin: "6px 0", fontSize: 14 }}>
        {f.quote ? `"${f.quote}"` : <span className="muted">no sentence quoted</span>}
      </blockquote>
      <div style={{ fontSize: 12 }}><SourceLink url={f.source_url} /></div>
    </li>
  );
}

export function ObsolescenceStage({ job, me, busy, error, onRun, onSearch, children }: {
  job: UC5Job; me?: Me | null; busy: boolean; error: string | null; onRun: () => void; onSearch: () => void;
  /** Actions on the finding, such as opening a case. */
  children?: React.ReactNode;
}) {
  // While sign-in is still loading `me` is undefined, and the actions stay disabled until it is known.
  const action = (label: string, onClick: () => void) =>
    <RoleButton role="engineer" me={me} onClick={onClick} disabled={busy}>{label}</RoleButton>;
  const result = job.obsolescence;
  const words = result ? STATUS_WORDS[result.status] ?? { label: result.status.replace(/_/g, " "), tone: "info" } : null;
  const lead = result?.evidence ?? null;
  const others = (result?.considered ?? []).filter(
    (f) => !(lead && f.source_url === lead.source_url && f.quote === lead.quote),
  );

  return (
    <div className="card">
      <div className="section-title">
        <span className="stage-chip">Stage 2</span><h3>Obsolescence Check</h3>
      </div>
      <Explainer>
        Status is one of obsolete, end of life announced, active, or no published notice found.
        There is deliberately no yes-or-no: the absence of a notice is not evidence that a part is
        current, and reporting it as active would be a confident guess. Every status comes with the
        sentence it was read from and where that sentence is.
      </Explainer>

      {error && (
        <p style={{ marginTop: 14 }} role="alert"><span className="pill fail">error</span> {error}</p>
      )}

      {result && words ? (
        <div style={{ marginTop: 14 }}>
          <p className="row" style={{ gap: 8 }}>
            <span className={`pill ${words.tone}`}>{words.label}</span>
            {result.disagreement && <span className="pill review">sources disagree</span>}
            <span className="muted" style={{ fontSize: 12 }}>
              {result.sources_read} source{result.sources_read === 1 ? "" : "s"} read
              {result.sources_failed ? `, ${result.sources_failed} could not be read` : ""}
            </span>
          </p>
          {lead?.successor_part && (
            <p style={{ fontSize: 14, marginTop: 8 }}>Successor named: <span className="mono">{lead.successor_part}</span></p>
          )}
          {lead || others.length ? (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {lead && <Finding f={lead} lead="deciding source" />}
              {others.map((f, i) => <Finding key={`${f.source_url}-${i}`} f={f} />)}
            </ul>
          ) : (
            <p className="muted" style={{ fontSize: 14, marginTop: 8 }}>No source quoted a lifecycle statement for this part.</p>
          )}
          {job.stage === "market" || job.stage === "ltb" ? (
            <p style={{ marginTop: 12 }}><span className="pill info">market search complete</span></p>
          ) : (
            <div style={{ marginTop: 12 }}>{action("Start market search", onSearch)}</div>
          )}
          {children}
        </div>
      ) : (
        <div style={{ marginTop: 14 }}>
          {action(busy ? "Checking..." : "Run obsolescence check", onRun)}
        </div>
      )}
    </div>
  );
}

export function MarketStage({ job, listStatus }: { job: UC5Job; listStatus?: ApprovedListStatus | null }) {
  const candidates = job.candidates ?? [];
  return (
    <div className="card">
      <div className="section-title">
        <span className="stage-chip">Stage 3</span><h3>Market Search Results</h3>
      </div>
      <Explainer>
        Each option is what a source says can be done about this part, with the sentence that says
        so. Options are ordered by the SD-22 resolutions from cheapest to most involved, then by how
        much other equipment has to change. Choosing one commits money, so this stage proposes and a
        person decides.
      </Explainer>
      <div style={{ marginTop: 12 }}><ApprovedListNote status={listStatus} /></div>
      <div style={{ marginTop: 16 }}>
        {candidates.length ? (
          <MarketResults candidates={candidates} />
        ) : (
          <div className="mismatch" style={{ padding: 16 }}>
            <p style={{ fontSize: 14 }}><strong>No replacement offered.</strong></p>
            <p className="muted" style={{ fontSize: 13, marginTop: 6 }}>
              None of the sources read named a successor, a last time buy or any other resolution for
              this part. The next step is to find a new item or a new source.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
